import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { DataTable, DrillLinks, HealthBadge, KpiGrid, KpiTile, Section } from './ManagementWidgets'
import { applyTextFilters, at, exportToSpreadsheet, flattenMetrics, formatDate, metricText } from './managementUtils'

const LINKS = [
  { label: 'All projects', to: '/projects' },
  { label: 'Milestones', to: '/projects/milestones' },
  { label: 'Resource allocations', to: '/projects/resource-allocations' }
]

export const projectLink = (value, row) => (
  row.projectId ? <Link to={`/projects/view/${row.projectId}`}>{value || row.projectNumber || 'View project'}</Link> : (value || '—')
)

const HEALTH_FILTERS = ['All', 'Red', 'Amber', 'Green', 'Grey']

const ProjectPortfolioHealthReport = () => {
  const report = useManagementReport(managementService.getProjectPortfolio)
  const [health, setHealth] = useState('All')

  const onExport = (data) => exportToSpreadsheet('project-portfolio', {
    Summary: flattenMetrics(data.summary),
    Projects: (data.projects || []).map((p) => ({
      'Project No': p.projectNumber,
      Project: p.projectName,
      Customer: p.customerName,
      'Project manager': p.projectManager?.name || '',
      Status: p.status,
      Health: p.derivedHealth,
      'Planned end': formatDate(p.plannedEndDate),
      'Schedule overdue': p.scheduleOverdue ? 'Yes' : 'No',
      SOW: metricText(p.sowValue, 'currency'),
      Invoiced: metricText(p.invoicedTaxable, 'currency'),
      Outstanding: metricText(p.outstanding, 'currency'),
      'Unbilled SOW': metricText(p.unbilledSow, 'currency'),
      Cost: metricText(p.projectCost, 'currency'),
      'Margin %': metricText(p.marginPercent, 'percent'),
      'Milestones overdue': metricText(p.milestones?.overdue),
      Gaps: (p.gaps || []).join('; ')
    }))
  })

  const filterRows = useMemo(() => (rows) => {
    const byText = applyTextFilters(rows, report.filters)
    return health === 'All' ? byText : byText.filter((r) => r.derivedHealth === health)
  }, [report.filters, health])

  return (
    <ManagementReportShell
      title="Project Portfolio Health"
      subtitle="Delivery health, milestones, billing coverage and margin across active projects"
      report={report}
      onExport={onExport}
    >
      {(d) => {
        const s = d.summary || {}
        const byHealth = s.byHealth || {}
        const rows = filterRows(d.projects || [])
        return (
          <>
            <DrillLinks links={LINKS} />
            <Section title="Portfolio summary">
              <KpiGrid>
                <KpiTile label="Total projects" metric={s.totalProjects} />
                <KpiTile label="Active" metric={s.activeProjects} />
                <KpiTile label="Overdue (schedule)" metric={s.overdueProjects} tone="red" />
                <KpiTile label="Without PM" metric={s.projectsWithoutManager} tone="amber" />
                <KpiTile label="Without end date" metric={s.projectsWithoutEndDate} tone="amber" />
                <KpiTile label="Total SOW" metric={s.totalSow} format="currency" />
                <KpiTile label="Invoiced (pre-tax)" metric={s.totalInvoicedTaxable} format="currency" />
                <KpiTile label="Outstanding" metric={s.totalOutstanding} format="currency" to="/management/reports/customer-collection" />
                <KpiTile label="Portfolio cost" metric={s.portfolioCost} format="currency" />
                <KpiTile label="Portfolio margin" metric={s.portfolioMargin} format="currency" />
                <KpiTile label="Milestones complete" metric={at(s, 'milestones.completionPercent')} format="percent" />
                <KpiTile label="Milestones overdue" metric={at(s, 'milestones.overdue')} tone="amber" />
              </KpiGrid>
              <div className="mis-report-period" style={{ marginTop: 12 }}>
                {['Red', 'Amber', 'Green', 'Grey'].map((h) => (
                  <span key={h}><HealthBadge status={h} label={`${h}: ${byHealth[h] ?? 0}`} /></span>
                ))}
              </div>
            </Section>

            <Section
              title="Projects"
              subtitle="Health is derived from schedule, overdue milestones and overdue receivables"
              actions={(
                <div className="mis-segmented no-print">
                  {HEALTH_FILTERS.map((h) => (
                    <button key={h} type="button" className={health === h ? 'active' : ''} onClick={() => setHealth(h)}>{h}</button>
                  ))}
                </div>
              )}
            >
              <DataTable
                rows={rows}
                maxRows={30}
                emptyText="No projects match the selected filters."
                columns={[
                  { key: 'projectName', label: 'Project', render: projectLink },
                  { key: 'customerName', label: 'Customer' },
                  { key: 'projectManager', label: 'PM', render: (v) => v?.name || <span className="mis-muted">Not assigned</span> },
                  { key: 'derivedHealth', label: 'Health', type: 'health' },
                  { key: 'plannedEndDate', label: 'Planned end', type: 'date' },
                  { key: 'milestones', label: 'Milestones (done / overdue)', render: (v) => `${metricText(v?.completed)} / ${metricText(v?.overdue)}` },
                  { key: 'sowValue', label: 'SOW', format: 'currency' },
                  { key: 'invoicedTaxable', label: 'Invoiced', format: 'currency' },
                  { key: 'outstanding', label: 'Outstanding', format: 'currency' },
                  { key: 'marginPercent', label: 'Margin %', format: 'percent' },
                  { key: 'gaps', label: 'Data gaps', render: (v) => (v?.length ? v.join(', ') : '—') }
                ]}
              />
            </Section>
          </>
        )
      }}
    </ManagementReportShell>
  )
}

export default ProjectPortfolioHealthReport
